/**
 * True when an analytics value is a real, usable configuration — i.e. not
 * absent, empty, or a placeholder id.
 *
 * `default-tenant-config.json` (and any unfilled tenant `analytics` block) ships
 * placeholder ids whose token is all X's — e.g. `GTM-XXXXXXX`, `G-XXXXXXXXXX`,
 * `UA-XXXXXXX-X`, `AW-XXXXXXXXX` — including inside the `gtmHead` / `gtmBody`
 * HTML blobs. Initializing those makes GTM/GA request `gtm.js?id=GTM-XXXXXXX`
 * on every reload and log analytics errors, even though nothing is configured.
 *
 * A non-empty value with no recognizable placeholder id is treated as configured
 * on purpose, so a genuinely malformed (real-but-wrong) value still enters the
 * init path and surfaces its load error rather than being silently swallowed.
 */
const ANALYTICS_ID = /\b(?:GTM|UA|AW|G)-[A-Za-z0-9]+(?:-[A-Za-z0-9]+)?/gi;
const ALL_X_TOKEN = /^X+(?:-X+)?$/i;

export const isAnalyticsConfigured = (value?: string | null): value is string => {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;

  const ids = trimmed.match(ANALYTICS_ID);
  // Non-empty but no recognizable analytics id: treat as configured so a
  // malformed value is not silently skipped (its failure stays visible).
  if (!ids) return true;

  // Configured iff at least one id is a real token, not an all-X placeholder.
  return ids.some((id) => !ALL_X_TOKEN.test(id.replace(/^(?:GTM|UA|AW|G)-/i, "")));
};
